import { useState, useCallback } from 'react';

interface ShareButtonsProps {
  url: string;
  title: string;
  description: string;
  variant?: 'compact' | 'full';
  redditSubreddit?: string;
}

export function ShareButtons({
  url,
  title,
  description,
  variant = 'full',
  redditSubreddit = 'ultralight',
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write failed - silently fail
    }
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({
        title,
        text: description,
        url,
      });
    } catch {
      // Share cancelled or failed - silently fail
    }
  }, [title, description, url]);

  // Build Twitter share URL
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `${title}\n\n${description}`
  )}&url=${encodeURIComponent(url)}`;

  // Build Reddit share URL for r/ultralight
  const redditShareUrl = `https://reddit.com/r/${redditSubreddit}/submit?url=${encodeURIComponent(
    url
  )}&title=${encodeURIComponent(title)}`;

  // Check if Web Share API is available
  const canNativeShare =
    typeof navigator !== 'undefined' &&
    'share' in navigator &&
    'canShare' in navigator &&
    navigator.canShare?.({ url, title, text: description });

  const isCompact = variant === 'compact';

  return (
    <div className={`flex items-center ${isCompact ? 'gap-2' : 'gap-3'}`}>
      {/* Copy URL Button */}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy link'}
        className={`flex items-center justify-center ${isCompact ? 'p-2' : 'px-3 py-2'} bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors`}
      >
        {copied ? (
          <>
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {!isCompact && <span className="ml-2 text-sm text-green-400">Copied!</span>}
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {!isCompact && <span className="ml-2 text-sm">Copy link</span>}
          </>
        )}
      </button>

      {/* Twitter/X Share */}
      <a
        href={twitterShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Twitter"
        className={`flex items-center justify-center ${isCompact ? 'p-2' : 'px-3 py-2'} bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        {!isCompact && <span className="ml-2 text-sm">Twitter</span>}
      </a>

      {/* Reddit Share */}
      <a
        href={redditShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Reddit"
        className={`flex items-center justify-center ${isCompact ? 'p-2' : 'px-3 py-2'} bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
        {!isCompact && <span className="ml-2 text-sm">Reddit</span>}
      </a>

      {/* Native Share (mobile) */}
      {canNativeShare && (
        <button
          type="button"
          onClick={handleNativeShare}
          aria-label="Share"
          className={`flex items-center justify-center ${isCompact ? 'p-2' : 'px-3 py-2'} bg-orange-500 hover:bg-orange-400 text-black rounded-lg transition-colors`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          {!isCompact && <span className="ml-2 text-sm font-medium">Share</span>}
        </button>
      )}
    </div>
  );
}
